import { RouterView, type RouterOptions } from "vue-router";
import EntityAudits from "@/components/EntityAudits.vue";
import { EntityNotes } from "@modernary/mui/components/note";

const names = {
  facilitySites: "FacilitySite.FacilitySites",
  list: "FacilitySite.FacilitySiteList",
  new: "FacilitySite.FacilitySiteNew",
  base: "FacilitySite.FacilitySiteBase",
  view: "FacilitySite.FacilitySiteView",
  hub: "FacilitySite.FacilitySiteHub",
  edit: "FacilitySite.FacilitySiteEdit",
  affiliations: "FacilitySite.FacilitySiteAffiliations",
  affiliationEdit: "FacilitySite.FacilitySiteAffiliationEdit",
  affiliationNew: "FacilitySite.FacilitySiteAffiliationNew",
  affiliationSelect: "FacilitySite.FacilitySiteAffiliationSelect",
  notes: "FacilitySite.FacilitySiteNotes",
  noteEdit: "FacilitySite.FacilitySiteNoteEdit",
  noteNew: "FacilitySite.FacilitySiteNoteNew",
  materialsAccepted: "FacilitySite.FacilitySiteMaterialsAccepted",
  materialsAcceptedEdit: "FacilitySite.FacilitySiteMaterialsAcceptedEdit",
  disposalReports: "FacilitySite.FacilitySiteDisposalReports",
  inspections: "FacilitySite.FacilitySiteInspections",
  applications: "FacilitySite.FacilitySiteApplications",
  complaints: "FacilitySite.FacilitySiteComplaints",
  users: "FacilitySite.FacilitySiteUsers",
  userNew: "FacilitySite.FacilitySiteUserNew",
  audit: "FacilitySite.FacilitySiteAudit",
};

export { names as facilitySiteRouteNames };

const facilitySiteRoutes: RouterOptions["routes"] = [
  {
    path: "/facilitySites",
    name: names.facilitySites,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.list },
    children: [
      {
        path: "list",
        name: names.list,
        meta: {
          title: "Facilities",
          lookupGroups: ["FacilityPhysicalStatus", "FacilityPermitStatus", "FacilitySiteType", "County", "State"],
        },
        component: () => import("@/views/facilitySite/FacilitySiteList.vue"),
      },
      {
        path: "new",
        component: () => import("@/views/facilitySite/FacilitySiteBase.vue"),
        redirect: { name: names.new },
        props: true,
        children: [
          {
            path: "",
            name: names.new,
            meta: { title: "New Facility", isNew: true },
            component: () => import("@/views/facilitySite/FacilitySiteEdit.vue"),
          },
        ],
      },
      {
        path: ":id(\\d+)",
        name: names.base,
        component: () => import("@/views/facilitySite/FacilitySiteBase.vue"),
        redirect: { name: names.view },
        children: [
          {
            path: "view",
            name: names.view,
            meta: { title: "Facility View" },
            component: () => import("@/views/facilitySite/FacilitySiteView.vue"),
          },
          {
            path: "hub",
            name: names.hub,
            meta: { title: "Facility Hub", isForm: true },
            component: () => import("@/views/facilitySite/FacilitySiteHub.vue"),
          },
          {
            path: "edit",
            name: names.edit,
            meta: { title: "Facility Edit", isForm: true },
            component: () => import("@/views/facilitySite/FacilitySiteEdit.vue"),
          },
          {
            path: "affiliations",
            name: names.affiliations,
            meta: { title: "Facility Site Affiliations" },
            component: () => import("@/views/facilitySite/FacilitySiteAffiliations.vue"),
          },
          {
            path: "affiliations/:affiliationId(\\d+)/:entityType(organization|person)",
            name: names.affiliationEdit,
            meta: { title: "Edit Facility Site Affiliation", isForm: true },
            component: () => import("@/views/facilitySite/FacilitySiteAffiliation.vue"),
          },
          {
            path: "affiliations/new/:entityType(organization|person)",
            name: names.affiliationNew,
            meta: { title: "Create Facility Site Affiliation", isForm: true },
            component: () => import("@/views/facilitySite/FacilitySiteAffiliation.vue"),
          },
          {
            path: "affiliations/select",
            name: names.affiliationSelect,
            meta: { title: "Create Facility Site Affiliation", isForm: true },
            component: () => import("@/views/facilitySite/FacilitySiteAffiliationSelect.vue"),
          },
          {
            path: "notes",
            name: names.notes,
            meta: { title: "Facility Site Internal Notes" },
            component: {
              render: () => <EntityNotes idField="facilitySiteId" routeNamePrefix="FacilitySite.FacilitySiteNote" />,
            },
          },
          {
            path: "notes/:annotationId(\\d+)",
            name: names.noteEdit,
            meta: { title: "Edit Facility Site Internal Note", isForm: true },
            component: () => import("@/views/facilitySite/FacilitySiteNote.vue"),
          },
          {
            path: "notes/new",
            name: names.noteNew,
            meta: { title: "Create Facility Site Internal Note", isForm: true },
            component: () => import("@/views/facilitySite/FacilitySiteNote.vue"),
          },
          {
            path: "materialsAccepted",
            name: names.materialsAccepted,
            meta: { title: "Facility Site Material Accepted" },
            component: () => import("@/views/facilitySite/FacilitySiteMaterialsAccepted.vue"),
          },
          {
            path: "materialsAccepted/edit",
            name: names.materialsAcceptedEdit,
            meta: { title: "Edit Facility Site Material Accepted", isForm: true },
            component: () => import("@/views/facilitySite/FacilitySiteMaterialsAcceptedEdit.vue"),
          },
          {
            path: "disposalReports",
            name: names.disposalReports,
            meta: { title: "Facility Site Disposal Reports" },
            component: () => import("@/views/facilitySite/DisposalReportChildList.vue"),
          },
          {
            path: "inspections",
            name: names.inspections,
            meta: { title: "Facility Site Inspections" },
            component: () => import("@/views/facilitySite/InspectionsChildList.vue"),
          },
          {
            path: "applications",
            name: names.applications,
            meta: { title: "Facility Site Applications" },
            component: () => import("@/views/facilitySite/ApplicationsChildList.vue"),
          },
          {
            path: "complaints",
            name: names.complaints,
            meta: { title: "Facility Site Complaints" },
            component: () => import("@/views/facilitySite/ComplaintsChildList.vue"),
          },
          {
            path: "users",
            name: names.users,
            meta: { title: "Facility Site Users" },
            component: () => import("@/views/facilitySite/FacilitySiteUsers.vue"),
          },
          {
            path: "users/new",
            name: names.userNew,
            meta: { title: "Add Facility Site User", isForm: true },
            component: () => import("@/views/facilitySite/FacilitySiteUser.vue"),
          },
          {
            path: "activity",
            name: names.audit,
            meta: { title: "Facility Site Audit Activity" },
            component: {
              render: () => <EntityAudits entityName="FacilitySite" />,
            },
          },
          // {
          //   path: "documents",
          //   name: "FacilitySiteDocuments",
          //   meta: { title: "Facility Documents" },
          //   component: { render: () => <div>docs</div> },
          // },
        ],
      },
    ],
  },
];
export default facilitySiteRoutes;
